import { applySnapshot, flow, Instance, types } from "mobx-state-tree";
import { api } from "../service/api";
import { ContextModel, SearchModel } from "../models/Common";
import { ActiveProgramsModel, MyClientsModel, MyClientsParamsModel, RowsModel } from "../models/MyClients";
import {
  IClientCSVData,
  IMyClientsParams,
  IMyClientsSearchParams,
  IRowMyClientsReports,
} from "../constants/interfaces";
import { serviceCoach } from "../utils/env";
import { formatDate } from "../utils/date";
import { getArrow } from "../utils/common";

const MyClientsStore = types
  .model({
    items: types.array(MyClientsModel),
    rows: types.optional(types.array(RowsModel), []),
    context: types.maybe(ContextModel),
    isLoading: types.optional(types.boolean, false),
    activePrograms: types.optional(types.array(ActiveProgramsModel), []),
    search: types.optional(types.array(SearchModel), []),
    emailSearch: types.optional(types.array(SearchModel), []),
    params: types.optional(MyClientsParamsModel, {}),
  })
  .views((self) => ({}))
  .actions((self) => ({
    buildUrl(params: IMyClientsParams, url: string) {
      const urlDefault = `${serviceCoach}${url}`;
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        const value = params[key as keyof typeof params];
        if (value !== undefined && value !== null) {
          if (key === "filter" && typeof value === "object") {
            Object.entries(value).forEach(([filterKey, filterValue]) => {
              if (filterValue !== undefined && filterValue !== null) {
                queryParams.append(filterKey, filterValue.toString());
              }
            });
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });

      return `${urlDefault}?${queryParams.toString()}`;
    },
  }))
  .actions((self) => ({
    fetch: flow(function* (params: IMyClientsParams) {
      try {
        const url = self.buildUrl(params, "/api/v1/my-clients");
        const { data } = yield api.get(url);
        const { context, items } = data;

        let rowsAux: Instance<typeof RowsModel>[] | [] = [];
        self.isLoading = true;

        if (items?.length && items.length > 0) {
          items?.forEach((item: Instance<typeof MyClientsModel>, index: number) => {
            let row: Instance<typeof RowsModel> = {
              id: 0,
              fullName: "-",
              activeProgram: "-",
              email: "-",
              programStartDate: "-",
              lastLoginDate: "-",
              loginsPast30Days: 0,
              status: "-",
              redirect: 0,
            };

            row.id = item.id;
            row.fullName = item.fullName;
            row.activeProgram = item.activeProgram;
            row.email = item?.email ?? "-";
            row.programStartDate = item?.programStartDate ? formatDate(item.programStartDate) : "-";
            row.lastLoginDate = item.lastLoginDate ? item?.lastLoginDate : "-";
            row.loginsPast30Days = item.loginsPast30Days ? item?.loginsPast30Days : 0;
            row.status = item?.status ?? "-";
            row.redirect = item.id;

            rowsAux = [...rowsAux, row];
          });
        }
        applySnapshot(self, {
          ...self,
          items,
          rows: rowsAux,
          context,
          isLoading: false,
          params,
        });
      } catch (error) {
        console.log("Error fetch clients table:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    fetchCSVData: flow(function* (params: IMyClientsParams) {
      try {
        const url = self.buildUrl(params, "/api/v1/my-clients");
        const { data } = yield api.get(url);
        const { items } = data;

        let csvData: IClientCSVData[] = [];
        self.isLoading = true;
        //TODO: At the client's request, the 'status' value is temporarily removed. The task is FF-206
        if (items?.length && items.length > 0) {
          items?.forEach((item: IClientCSVData) => {
            let row: IClientCSVData = {
              fullName: "-",
              activeProgram: "-",
              email: "-",
              programStartDate: "-",
              lastLoginDate: "-",
              loginsPast30Days: "-",
              // status: "-",
              phone: "-",
              dateOfBirth: "-",
            };

            row.fullName = item?.fullName;
            row.activeProgram = item?.activeProgram;
            row.email = item?.email ?? "-";
            row.programStartDate = item?.programStartDate ? formatDate(item.programStartDate) : "-";
            row.lastLoginDate = item?.lastLoginDate ? item?.lastLoginDate : "-";
            row.loginsPast30Days = item.loginsPast30Days ? item?.loginsPast30Days.toString() : "0";
            // row.status = item?.status ?? "-";
            row.phone = item?.phone ?? "-";
            row.dateOfBirth = item?.dateOfBirth ? formatDate(item?.dateOfBirth) : "-";

            csvData = [...csvData, row];
          });
        }
        return csvData;
      } catch (error) {
        console.log("Error fetchCSVData:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    fetchActivePrograms: flow(function* (params: IMyClientsParams) {
      try {
        self.isLoading = true;
        const url = self.buildUrl(params, "/api/v1/active-programs");
        const { data } = yield api.get(url);

        applySnapshot(self, {
          ...self,
          activePrograms: data,
        });
      } catch (error) {
        console.log("Error active programs:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    fetchBirthdayReport: flow(function* (params: IMyClientsParams) {
      try {
        self.isLoading = true;
        const url = self.buildUrl(params, "/api/v1/report/my-clients/birthday");
        const { data } = yield api.get(url);

        const formattedData = data.items.map((item: any) => {
          return {
            fullName: item?.fullName && item?.fullName !== "" ? item?.fullName : "-",
            dateOfBirth: item?.dateOfBirth && item?.dateOfBirth !== "" ? item?.dateOfBirth : "-",
            programStartDate:
              item?.programStartDate && item?.programStartDate !== "" ? formatDate(item.programStartDate) : "-",
            activeProgram: item?.activeProgram && item?.activeProgram !== "" ? item?.activeProgram : "-",
            email: item?.email && item?.email !== "" ? item?.email : "-",
            phone: item?.phone && item?.phone !== "" ? item?.phone : "-",
            id: item.id,
          };
        });

        applySnapshot(self, {
          ...self,
          params,
        });
        return { ...data, items: formattedData };
      } catch (error) {
        console.log("Error fetchBirthdayReport:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    fetchCancellationRiskReport: flow(function* (params: IMyClientsParams) {
      try {
        self.isLoading = true;
        const url = self.buildUrl(params, "/api/v1/report/my-clients/cancellation-risk");
        const { data } = yield api.get(url);

        const formattedData = data.items.map((item: any) => {
          return {
            fullName: item?.fullName && item?.fullName !== "" ? item?.fullName : "-",
            lastLoginDate: item?.lastLoginDate && item?.lastLoginDate !== "" ? item?.lastLoginDate : "-",
            startDate: item?.startDate && item?.startDate !== "" ? formatDate(item.startDate) : "-",
            email: item?.email && item?.email !== "" ? item?.email : "-",
            phone: item?.phone && item?.phone !== "" ? item?.phone : "-",
            id: item?.userId,
          };
        });

        applySnapshot(self, {
          ...self,
          params,
        });
        return { ...data, items: formattedData };
      } catch (error) {
        console.log("Error fetchCancellationRiskReport:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    fetchCancelledReport: flow(function* (params: IMyClientsParams) {
      try {
        self.isLoading = true;
        const url = self.buildUrl(params, "/api/v1/report/my-clients/cancelled");
        const { data } = yield api.get(url);

        const formattedData = data.items.map((item: any) => {
          return {
            fullName: item?.fullName && item?.fullName !== "" ? item?.fullName : "-",
            vipCancellationDate:
              item?.vipCancellationDate && item?.vipCancellationDate !== "" ? item?.vipCancellationDate : "-",
            email: item?.email && item?.email !== "" ? item?.email : "-",
            phone: item?.phone && item?.phone !== "" ? item?.phone : "-",
            id: item?.id,
          };
        });

        applySnapshot(self, {
          ...self,
          params,
        });
        return { ...data, items: formattedData };
      } catch (error) {
        console.log("Error fetchStarReport:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    fetchStarReport: flow(function* (params: IMyClientsParams) {
      try {
        self.isLoading = true;
        const url = self.buildUrl(params, "/api/v1/report/my-clients/star");
        const { data } = yield api.get(url);

        const formattedData = data.items.map((item: any) => {
          const formatCategory = (category: any) => ({
            stars: category?.stars ?? 0,
            trendIndicator: category?.trendIndicator || "STRAIGHT",
          });

          return {
            fullName: item?.fullName && item?.fullName !== "" ? item?.fullName : "-",
            stars: item?.stars && item?.stars !== "" ? item?.stars : 0,
            trendIndicator: item?.trendIndicator || "STRAIGHT",
            activeProgram: item?.activeProgram && item?.activeProgram !== "" ? item?.activeProgram : "-",
            lastLoginDate: item?.lastLoginDate && item?.lastLoginDate !== "" ? item?.lastLoginDate : "-",
            programStartDate:
              item?.programStartDate && item?.programStartDate !== "" ? formatDate(item.programStartDate) : "-",
            email: item?.email && item?.email !== "" ? item?.email : "-",
            phone: item?.phone && item?.phone !== "" ? item?.phone : "-",
            id: item.id,
            workout: formatCategory(item?.workout),
            food: formatCategory(item?.food),
            walking: formatCategory(item?.walking),
            fasting: formatCategory(item?.fasting),
            water: formatCategory(item?.water),
          };
        });
        applySnapshot(self, {
          ...self,
          params,
        });
        return { ...data, items: formattedData };
      } catch (error) {
        console.log("Error fetchStarReport:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    fetchClientReports: flow(function* (params: IMyClientsParams, reportType: string, csv: boolean = false) {
      try {
        self.isLoading = true;
        const baseUrl = `/api/v1/report/my-clients/${reportType}`;
        const url = self.buildUrl(params, baseUrl);
        const { data } = yield api.get(url);
        if (csv) {
          const result: IRowMyClientsReports[] = data.items.map((row: IRowMyClientsReports) => {
            row["dateOfBirth"] && (row["dateOfBirth"] = formatDate(row["dateOfBirth"]));
            row["vipCancellationDate"] && (row["vipCancellationDate"] = formatDate(row["vipCancellationDate"]));
            row["programStartDate"] && (row["programStartDate"] = formatDate(row["programStartDate"]));
            row["lastLoginDate"] && (row["lastLoginDate"] = formatDate(row["lastLoginDate"]));
            return row;
          });
          if (reportType === "star") {
            result.forEach((row: any) => {
              row.trendIndicator = getArrow(row.trendIndicator);

              const starColumns = ["workout", "food", "fasting", "water", "walking"];

              starColumns.forEach((col) => {
                if (row[col] && typeof row[col] === "object") {
                  const stars = row[col].stars ?? 0;
                  const trend = row[col].trendIndicator;

                  row[col] = `${stars} ${getArrow(trend)}`;
                }
              });
            });
          }
          return result;
        }
        applySnapshot(self, {
          ...self,
          params,
        });
        return data;
      } catch (error) {
        console.log("Error fetchClientReport:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    fetchBabyCoachReport: flow(function* (params: IMyClientsParams) {
      try {
        self.isLoading = true;
        const url = self.buildUrl(params, "/api/v1/report/my-clients/baby-coaches");
        const { data } = yield api.get(url);

        const formattedData = data.items.map((item: any) => {
          return {
            fullName: item?.fullName && item?.fullName !== "" ? item?.fullName : "-",
            lastLoginDate: item?.lastLoginDate && item?.lastLoginDate !== "" ? item?.lastLoginDate : "-",
            stars: item?.stars && item?.stars !== "" ? item?.stars : "-",
            email: item?.email && item?.email !== "" ? item?.email : "-",
            phone: item?.phone && item?.phone !== "" ? item?.phone : "-",
            id: item?.id,
          };
        });

        applySnapshot(self, {
          ...self,
          params,
        });
        return { ...data, items: formattedData };
      } catch (error) {
        console.log("Error fetchBabyCoachReport:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    fetchSearch: flow(function* (params: IMyClientsSearchParams) {
      try {
        self.isLoading = true;
        const url = self.buildUrl(params, "/api/v1/my-clients/search");
        const { data } = yield api.get(url);

        applySnapshot(self, {
          ...self,
          search: data,
        });
      } catch (error) {
        console.log("Error my clients search:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    fetchEmailSearch: flow(function* (params: IMyClientsParams) {
      try {
        self.isLoading = true;
        const url = self.buildUrl(params, "/api/v1/my-clients/email-search");
        const { data } = yield api.get(url);

        applySnapshot(self, {
          ...self,
          emailSearch: data,
        });
      } catch (error) {
        console.log("Error my clients email search:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    clear: function () {
      applySnapshot(self, {
        ...self,
        items: [],
        rows: [],
        context: undefined,
      });
    },
  }));

export const ClientsStore = MyClientsStore.create();
